import { useCallback, useEffect, useState } from "react";
import { Drop, Sparkle, Trash, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BLANK = { name: "", brand: "", category: "water", price: "", currency: "usd",
  size: "", local: true, market: "US", supplier: "", commission_pct: "15",
  commission_flat: "0", pay_mode: "app" };

export default function Refreshments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("board");
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [hunt, setHunt] = useState({ category: "coffee", market: "US", currency: "usd", local_area: "" });
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/refreshments", { params: category ? { category } : {} })
      .then(({ data: d }) => setData(d)).catch(oops);
  }, [category]);
  const loadOrders = useCallback(() => {
    api.get("/refreshments/orders").then(({ data: d }) => setOrders(d)).catch(oops);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "orders") loadOrders(); }, [view, loadOrders]);
  useEffect(() => {
    if (view === "money" && !ledger) api.get("/refreshments/commissions").then(({ data: d }) => setLedger(d)).catch(oops);
  }, [view, ledger]);
  useEffect(() => {
    if (view === "hunt" && !suggestions) api.get("/refreshments/suggestions").then(({ data: d }) => setSuggestions(d)).catch(oops);
  }, [view, suggestions]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/refreshments", {
        ...form, price: Number(form.price), commission_pct: Number(form.commission_pct),
        commission_flat: Number(form.commission_flat), brand: form.brand || null,
        size: form.size || null, supplier: form.supplier || null,
      });
      setForm(BLANK); toast.success("On the board with AI copy and a picture"); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const findThem = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/refreshments/suggest", { ...hunt, local_area: hunt.local_area || null });
      setSuggestions(d.suggestions); toast.success(`${d.suggestions.length} suggestions from ${d.engine_label}`);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/refreshments/suggestions/${id}/approve`);
      setSuggestions((s) => s.filter((x) => x.id !== id));
      toast.success("On the board"); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const orderIt = async (item) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/refreshments/orders", {
        item_id: item.id, qty: 1, origin_url: window.location.origin });
      if (d.checkout_url) { window.location.href = d.checkout_url; return; }
      toast.success(`Ordered — pay ${d.symbol || ""}${d.crew_total} on delivery`);
      loadOrders();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const TABS = [["board", "On offer"], ["orders", "Orders"],
    ...(isAdmin ? [["hunt", "Find brands"], ["money", "Commission"]] : [])];

  return (
    <div className="space-y-5" data-testid="refreshments-page">
      <PageHeader overline="Commission only — we hold no stock" title="Drinks & Supplies" testid="refreshments-header"
        description="Water, sparkling water, soda, energy drinks, electrolytes, local coffee, paper towels, wipes, soap and cleaning supplies from ULINE and other suppliers. AI finds the brands, you approve them, and every order shows the crew price, our commission and the supplier's payout." />

      <div className="flex flex-wrap items-center gap-2">
        <a href="/refreshments" target="_blank" rel="noreferrer" data-testid="refreshments-public-link"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          Public board
        </a>
        <ShareBar url={`${BACKEND}/api/share/refreshments`} text="Water, energy drinks and local coffee"
          testid="refreshments-share" compact instagram />
        {isAdmin && (
          <Button variant="outline" data-testid="refreshments-refresh" disabled={busy}
            onClick={async () => { setBusy(true); try { await api.post("/refreshments/refresh"); toast.success("Board rewritten and pictures redrawn"); load(); } catch (e) { oops(e); } finally { setBusy(false); } }}>
            <Sparkle size={15} className="mr-1.5" /> Refresh the board
          </Button>
        )}
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="scroll-x w-full justify-start overflow-x-auto" data-testid="refreshments-tabs">
          {TABS.map(([key, label]) => (
            <TabsTrigger key={key} value={key} data-testid={`refreshments-tab-${key}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {view === "board" && (
        <div className="space-y-4" data-testid="refreshments-board">
          <div className="scroll-x flex gap-1.5 overflow-x-auto" data-testid="refreshments-filter">
            {[["", "Everything"], ...(data?.categories || []).map((c) => [c.key, c.label])].map(([key, label]) => (
              <button key={key || "all"} data-testid={`refreshments-category-${key || "all"}`} onClick={() => setCategory(key)}
                className={`tap-safe shrink-0 border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                  category === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>

          {data?.can_manage && (
            <div className="grid gap-2 border border-border bg-white p-4 sm:grid-cols-4" data-testid="refreshments-form">
              <Input data-testid="refreshments-brand" placeholder="Brand" value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })} />
              <Input data-testid="refreshments-name" placeholder="Name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <select data-testid="refreshments-category-select" className="border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {(data.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <Input data-testid="refreshments-price" type="number" placeholder="Unit price" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })} />
              <Input data-testid="refreshments-size" placeholder="Size / pack" value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })} />
              <Input data-testid="refreshments-supplier" placeholder="Supplier" value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
              <Input data-testid="refreshments-commission" type="number" placeholder="Commission %" value={form.commission_pct}
                onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} />
              <select data-testid="refreshments-pay-mode" className="border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={form.pay_mode} onChange={(e) => setForm({ ...form, pay_mode: e.target.value })}>
                <option value="app">Crew pays by card here</option>
                <option value="direct">Pays the supplier direct</option>
              </select>
              <Button data-testid="refreshments-add" onClick={add}
                disabled={busy || form.name.trim().length < 2 || !form.price}>
                {busy ? "Writing…" : "Add to board"}
              </Button>
            </div>
          )}

          {data?.items?.length === 0 ? (
            <EmptyState testid="refreshments-empty" title="Nothing on the board yet"
              description="Add a brand, or let AI find local brands for a category." />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="refreshments-list">
              {data?.items?.map((d, i) => (
                <div key={d.id} data-testid={`refreshment-${i}`} className="border border-border bg-white">
                  {d.image_url && <img src={`${BACKEND}${d.image_url}`} alt={d.name} className="h-36 w-full object-cover" />}
                  <div className="p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">
                      {d.category_label}{d.local ? " · local" : ""}
                    </p>
                    <p className="mt-0.5 font-semibold text-[#0F172A]">{d.brand ? `${d.brand} ` : ""}{d.name}</p>
                    <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{d.symbol}{d.price}
                      {d.size ? <span className="text-xs font-semibold text-slate-500"> · {d.size}</span> : null}</p>
                    {isAdmin && (
                      <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-500" data-testid={`refreshment-commission-${i}`}>
                        {d.commission_pct}% · {d.pay_mode === "app" ? "card here" : "pays supplier"}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Button size="sm" data-testid={`refreshment-order-${i}`} onClick={() => orderIt(d)} disabled={busy}>
                        Order one
                      </Button>
                      <ShareBar url={`${BACKEND}/api/share/drink/${d.id}`} text={`${d.brand || ""} ${d.name}`.trim()}
                        testid={`refreshment-share-${i}`} compact instagram />
                      {isAdmin && (
                        <button data-testid={`refreshment-delete-${i}`} onClick={async () => { try { await api.delete(`/refreshments/${d.id}`); load(); } catch (e) { oops(e); } }}
                          className="tap-safe text-xs font-semibold text-slate-500 hover:text-[#B3241B]">
                          <Trash size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "orders" && (
        orders?.length === 0 ? (
          <EmptyState testid="refreshment-orders-empty" title="No orders yet"
            description="Order something from the board and it appears here." />
        ) : (
          <ul className="space-y-2" data-testid="refreshment-orders">
            {orders?.map((o, i) => (
              <li key={o.id} data-testid={`refreshment-order-row-${i}`}
                className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                <span className="font-semibold text-[#0F172A]">{o.brand ? `${o.brand} ` : ""}{o.item_name}</span>
                <span className="text-slate-600">{o.qty} × · {o.currency} {o.crew_total}</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{o.payment_status}</span>
              </li>
            ))}
          </ul>
        )
      )}

      {view === "hunt" && (
        <div className="space-y-3" data-testid="refreshments-hunt">
          <div className="flex flex-wrap items-end gap-2 border border-border bg-white p-4">
            <select data-testid="refreshments-hunt-category" className="border border-border bg-white px-2 py-2 text-base md:text-sm"
              value={hunt.category} onChange={(e) => setHunt({ ...hunt, category: e.target.value })}>
              {(data?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <Input data-testid="refreshments-hunt-area" className="w-full sm:w-52" placeholder="Local area, e.g. Detroit"
              value={hunt.local_area} onChange={(e) => setHunt({ ...hunt, local_area: e.target.value })} />
            <Button data-testid="refreshments-hunt-go" onClick={findThem} disabled={busy}>
              <Sparkle size={15} className="mr-1.5" /> {busy ? "Looking…" : "Find brands"}
            </Button>
          </div>
          {suggestions?.length === 0 ? (
            <EmptyState testid="refreshments-suggestions-empty" title="Nothing suggested yet"
              description="Pick a category and AI will put four real brands up for approval." />
          ) : (
            <ul className="space-y-2" data-testid="refreshments-suggestions">
              {suggestions?.map((s, i) => (
                <li key={s.id} data-testid={`refreshment-suggestion-${i}`} className="border border-border bg-white p-3">
                  <p className="font-semibold text-[#0F172A]">{s.brand ? `${s.brand} — ` : ""}{s.name}{s.size ? ` · ${s.size}` : ""}</p>
                  <p className="text-sm text-slate-600">{s.why}</p>
                  <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{s.currency?.toUpperCase()} {s.price}{s.local ? " · local" : ""}</p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" data-testid={`refreshment-approve-${i}`} onClick={() => approve(s.id)} disabled={busy}>
                      <Check size={14} className="mr-1" /> Put on the board
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`refreshment-reject-${i}`}
                      onClick={async () => { try { await api.post(`/refreshments/suggestions/${s.id}/reject`); setSuggestions((x) => x.filter((y) => y.id !== s.id)); } catch (e) { oops(e); } }}>
                      <X size={14} className="mr-1" /> No
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {view === "money" && ledger && (
        <div className="space-y-4" data-testid="refreshments-commission">
          <div className="grid gap-3 sm:grid-cols-4">
            {[["Orders", ledger.totals.orders], ["Commission", ledger.totals.commission],
              ["Earned", ledger.totals.earned], ["Still due", ledger.totals.due]].map(([label, value]) => (
              <div key={label} className="border border-border bg-white p-3">
                <p className="overline text-slate-500">{label}</p>
                <p className="mt-1 text-xl font-bold text-[#0F172A]">{value}</p>
              </div>
            ))}
          </div>
          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">By brand</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700" data-testid="refreshments-by-brand">
              {ledger.by_brand.map((b, i) => (
                <li key={i}>{b.brand} · {b.orders} orders · ours {b.currency} {b.commission}</li>
              ))}
              {ledger.by_brand.length === 0 && <li className="text-slate-400">Nothing ordered yet</li>}
            </ul>
          </div>
          <ul className="space-y-2" data-testid="refreshments-ledger">
            {ledger.ledger.map((r, i) => (
              <li key={r.id} data-testid={`refreshment-ledger-${i}`}
                className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                <span className="inline-flex items-center gap-1.5 font-semibold text-[#0F172A]">
                  <Drop size={15} /> {r.brand ? `${r.brand} ` : ""}{r.item}
                </span>
                <span className="text-slate-600">{r.who} · {r.qty} × · {r.currency} {r.crew_total} → ours {r.commission}</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{r.commission_status}</span>
                {r.commission_status === "due" && (
                  <button data-testid={`refreshment-settle-${i}`} className="tap-safe text-xs font-semibold text-[#002FA7] hover:underline"
                    onClick={async () => { try { await api.post(`/refreshments/orders/${r.id}/settle`); setLedger(null); toast.success("Commission settled"); } catch (e) { oops(e); } }}>
                    Mark settled
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
